/**
 * Folder 52's words — every one from Boards 1–3
 * (`design/mockups/52-job-stages/`) or authorised by `docs/job-stages.md` §14
 * (`scripts/job-stages-authorised-strings.json`), and nowhere else in
 * `client/src/job-stages/**`. `npm run check:job-stages-strings` holds the
 * line: a board's word missing here, a word typed into a component, or a word
 * here that no board draws and §14 does not authorise, fails the build.
 *
 * Organised by board, then by component, in the order the boards draw them:
 * `COMMON` (words several places draw — one entry each), `JOB_STATUS` and
 * `QUOTE_TAG` (the chips), `ACTION` (Boards 1 and 2: every button and ⋯
 * row, with `actionLabel` / `actionSub` for a `JobActionItem`), `META` (the
 * job header's two additions), `BAND` (Board 2's bands, §6), `TOAST`,
 * `PROPOSAL_TAB`, then Board 3's popups — `APPROVE` (A), `MARK_QUOTED` (B),
 * `APPROVAL_SETTING` (C), `WITHDRAW` (D), `MARK_DECLINED` (E), `CANCEL_JOB`
 * (F).
 *
 * Rules for a caller (the visit page's, `client/src/visit/copy.ts`):
 * - A string with data in it is a function here; its fixed words are this
 *   file's and its data are parameters. Never assemble a sentence in a
 *   component — call the function.
 * - Parameters are strings exactly as the board prints them — `Q-2184`,
 *   `Oct 6`, `$2,180.00`, `Karen` — made from the server's values by
 *   `lib/format.ts` (`formatDateOnly`, `formatMoneyText`), never here.
 * - A sentence the board draws in two weights is two entries (`lead`, bold;
 *   `rest`, regular), so a component never decides where the bold ends.
 * - `<first name>` is the first name of the contact the proposal is prepared
 *   for (`readProposal`'s pick: the property's primary contact, else the first
 *   one linked). A quote with no contact has none, and the board draws no
 *   words for that case: `customer()` stands in with the words Board 3 C
 *   already uses for the person — *the customer* — and *Send* is §14's.
 */
import {
  DECLINE_REASONS, type DeclineReason, type JobActionItem, type JobActionKey, type MarkedHow, type OfficeApprovalHow,
} from '@rowted/shared'

// ===========================================================================
// Helpers — no words of their own
// ===========================================================================

/** *the customer* when there is no contact — Board 3 C's words for the person. */
const NO_CONTACT = 'the customer'

/** The person a sentence names: their first name, or *the customer*. */
export function customer(firstName: string | null | undefined): string {
  return firstName?.trim() || NO_CONTACT
}

/** The same, starting a sentence — *Karen*, or *The customer*. */
function Customer(firstName: string | null | undefined): string {
  const name = customer(firstName)
  return `${name.charAt(0).toUpperCase()}${name.slice(1)}`
}

/**
 * Somebody's words, quoted the way Board 2 prints them: the sentence closes
 * inside the marks — *“I chose someone else.”*, *“Karen is moving.”* — so
 * a reason typed with its own full stop keeps one, and one typed without
 * gets one.
 */
function said(text: string): string {
  const trimmed = text.trim()
  return /[.!?…]$/u.test(trimmed) ? `“${trimmed}”` : `“${trimmed}.”`
}

/** *A and B*, *A, B and C* — the visits a box names. */
function andList(items: readonly string[]): { head: string[]; last: string | null } {
  return { head: items.slice(0, -1), last: items[items.length - 1] ?? null }
}

/** A quote and its version, as every board prints it — *Q-2184 v2*. */
export function quoteVersion(reference: string, version: number): string {
  return `${reference} v${version}`
}

// ===========================================================================
// Words more than one place draws — one entry each
// ===========================================================================

export const COMMON = {
  cancel: 'Cancel',
  /** The faint word at the right of an optional field's label. */
  optional: 'optional',
  why: 'Why',
  /** *sent Oct 6 · viewed Oct 7* — Board 3 A and D's sub; *sent Oct 6* before it is viewed. */
  sentViewed: (sent: string, viewed?: string | null) =>
    (viewed ? `sent ${sent} · viewed ${viewed}` : `sent ${sent}`),
}

// ===========================================================================
// Board 1 — the job's status chip; Board 2 — the quote's tag
// ===========================================================================

export const JOB_STATUS = {
  requested: 'Requested',
  quoted: 'Quoted',
  approved: 'Approved',
  scheduled: 'Scheduled',
  declined: 'Declined',
  cancelled: 'Cancelled',
} as const satisfies Record<string, string>

export type FirstHalfStatus = keyof typeof JOB_STATUS

export const QUOTE_TAG = {
  /** *Draft v1*. */
  draft: (version: number) => `Draft v${version}`,
  viewed: 'Viewed',
  approved: 'Approved',
  declined: 'Declined',
  /** *Expired · v1*. */
  expired: (version: number) => `Expired · v${version}`,
  /** *Replaced · v3*. */
  replaced: (version: number) => `Replaced · v${version}`,
}

// ===========================================================================
// Boards 1 and 2 — the buttons and the ⋯ rows
// ===========================================================================

/** The page a ⋯ row is drawn on: some subs differ between the job and its quote. */
export type ActionPage = 'job' | 'quote'

export const ACTION = {
  edit: { label: 'Edit', sub: 'the job’s details' },
  newQuote: {
    label: 'New quote',
    /** From the job (Board 1). */
    another: (reference: string) => `another version of ${reference}`,
    /** From a quote (Board 2): *a copy of v2, as the next version*. */
    copy: (version: number) => `a copy of v${version}, as the next version`,
  },
  openQuote: {
    label: 'Open quote',
    /** *Q-2184 v2 · sent Oct 6*. */
    sub: (quote: string, sent: string) => `${quote} · sent ${sent}`,
  },
  /** §14. */
  openDraft: { label: 'Open draft', sub: (quote: string) => `${quote}, a draft` },
  /** *Open v2* — the newer version (`replaced_by_quote_id`). */
  openVersion: (version: number) => `Open v${version}`,
  openAgreement: {
    label: 'Open the agreement',
    /** *Q-2184 v2 · approved Oct 9*. */
    sub: (quote: string, approved: string) => `${quote} · approved ${approved}`,
  },
  /** *Send to Karen*; *Send* when the quote has no contact (§14). */
  send: (firstName: string | null | undefined) => (firstName?.trim() ? `Send to ${firstName.trim()}` : 'Send'),
  markQuoted: {
    label: 'Mark quoted',
    job: 'a quote went out another way',
    quote: 'it went out another way — no email',
  },
  approve: 'Approve',
  markDeclined: {
    label: 'Mark declined',
    /** *Karen said no*. */
    sub: (firstName: string | null | undefined) => `${Customer(firstName)} said no`,
  },
  withdrawQuote: {
    label: 'Withdraw quote',
    /** From the job: *take Q-2184 v2 back — the job goes back to Requested*. */
    job: (quote: string) => `take ${quote} back — the job goes back to Requested`,
    quote: 'take it back — the job goes back to Requested',
  },
  cancelJob: {
    label: 'Cancel job',
    quotes: 'stops the job and its quotes',
    work: 'stops the work and its visits',
  },
  reopenRequest: { label: 'Reopen request', sub: 'the job becomes a request again' },
  schedule: 'Schedule',
  startWork: { label: 'Start work', sub: 'today’s move, until part 2' },
  complete: { label: 'Complete', sub: 'today’s move, until part 2' },
  preview: { label: 'Preview', signed: 'the signed copy' },
  checkCrew: 'Check crew availability',
  /** Folder 51 — drawn on Boards 1 and 2; not offered until change orders are built. */
  changeOrder: 'Change order',
}

/** What a day prints as — the caller's formatter (`formatDateOnly`: *Oct 6*). */
export type DayFormat = (day: string | null | undefined) => string

/** The words on a button or a ⋯ row for one of `jobActions()`'s items. */
export function actionLabel(item: Pick<JobActionItem, 'key' | 'quote'>, firstName: string | null | undefined): string {
  const labels: Record<JobActionKey, string> = {
    edit: ACTION.edit.label,
    new_quote: ACTION.newQuote.label,
    open_quote: ACTION.openQuote.label,
    open_draft: ACTION.openDraft.label,
    open_version: ACTION.openVersion(item.quote?.version ?? 0),
    open_agreement: ACTION.openAgreement.label,
    send: ACTION.send(firstName),
    mark_quoted: ACTION.markQuoted.label,
    approve: ACTION.approve,
    mark_declined: ACTION.markDeclined.label,
    withdraw_quote: ACTION.withdrawQuote.label,
    cancel_job: ACTION.cancelJob.label,
    reopen_request: ACTION.reopenRequest.label,
    schedule: ACTION.schedule,
    start_work: ACTION.startWork.label,
    complete: ACTION.complete.label,
    preview: ACTION.preview.label,
    check_crew: ACTION.checkCrew,
  }
  return labels[item.key]
}

/**
 * A ⋯ row's one-line sub, as Boards 1 and 2 draw it — or null where the board
 * draws none (*Check crew availability*, a plain *Preview*). Buttons carry no
 * sub; only a ⋯ row asks for one.
 */
export function actionSub(
  item: Pick<JobActionItem, 'key' | 'quote' | 'copy' | 'stops' | 'signed'>,
  context: { page: ActionPage; firstName: string | null | undefined; day: DayFormat },
): string | null {
  const { quote } = item
  const named = quote ? quoteVersion(quote.reference, quote.version) : ''
  switch (item.key) {
    case 'edit': return ACTION.edit.sub
    case 'new_quote':
      if (item.copy && quote) return ACTION.newQuote.copy(quote.version)
      return quote ? ACTION.newQuote.another(quote.reference) : null
    case 'open_quote': return quote?.sentOn ? ACTION.openQuote.sub(named, context.day(quote.sentOn)) : null
    case 'open_draft': return quote ? ACTION.openDraft.sub(named) : null
    case 'open_agreement':
      return quote?.approvedOn ? ACTION.openAgreement.sub(named, context.day(quote.approvedOn)) : null
    case 'mark_quoted': return context.page === 'job' ? ACTION.markQuoted.job : ACTION.markQuoted.quote
    case 'mark_declined': return ACTION.markDeclined.sub(context.firstName)
    case 'withdraw_quote':
      return context.page === 'job' && quote ? ACTION.withdrawQuote.job(named) : ACTION.withdrawQuote.quote
    case 'cancel_job': return item.stops === 'work' ? ACTION.cancelJob.work : ACTION.cancelJob.quotes
    case 'reopen_request': return ACTION.reopenRequest.sub
    case 'start_work': return ACTION.startWork.sub
    case 'complete': return ACTION.complete.sub
    case 'preview': return item.signed ? ACTION.preview.signed : null
    default: return null
  }
}

// ===========================================================================
// Board 1 — the job header's meta additions (§3)
// ===========================================================================

export const META = {
  /** *Q-2184 v2 · sent Oct 6 · viewed Oct 7* — the quote with the customer, while Quoted. */
  withCustomer: (quote: string, sent: string, viewed?: string | null) =>
    (viewed ? `${quote} · sent ${sent} · viewed ${viewed}` : `${quote} · sent ${sent}`),
  /** *0 of 2 visits booked* — Approved and Scheduled (§14); *0 of 1 visit booked* for one (lead, Oct 30). */
  visitsBooked: (booked: number, total: number) =>
    (total === 1 ? `${booked} of ${total} visit booked` : `${booked} of ${total} visits booked`),
}

// ===========================================================================
// Board 2 — the bands under the quote's bar (§4, §6; §14 for the three no board draws)
// ===========================================================================

/** A band's sentence: `lead` in bold, then `rest`. */
export interface BandWords { lead: string; rest: string }

export const BAND = {
  /** Amber, on a draft while another version is out (§4 row 2). */
  withCustomer: (quote: string, firstName: string | null | undefined, sent: string, viewed?: string | null): BandWords => ({
    lead: `${quote} is with ${customer(firstName)}`,
    rest: viewed
      ? `— sent ${sent}, viewed ${viewed}. Sending this version expires it, so ${customer(firstName)} can only approve one.`
      : `— sent ${sent}. Sending this version expires it, so ${customer(firstName)} can only approve one.`,
  }),
  /** Grey — expired when a newer version was sent. */
  superseded: (date: string, newer: number): BandWords => ({
    lead: `Expired on ${date}, when v${newer} was sent.`,
    rest: 'It can’t be sent or approved.',
  }),
  /** Grey — *Valid until* passed. */
  byDate: (date: string, days: number): BandWords => ({
    lead: `Expired on ${date}`,
    rest: `— it was good for ${days} days. The job is a request again.`,
  }),
  /** Grey — a draft closed when another version was approved. */
  replaced: (date: string, firstName: string | null | undefined, approved: number): BandWords => ({
    lead: `Closed on ${date}, when ${customer(firstName)} approved v${approved}.`,
    rest: 'It can’t be sent or signed. To change the agreement, start a change order.',
  }),
  /** Grey — Withdraw quote (§14). */
  withdrawn: (date: string, name: string, why: string): BandWords => ({
    lead: `Withdrawn on ${date} by ${name}`,
    rest: `— ${said(why)} The job is a request again.`,
  }),
  /** Grey — a sent quote expired by Cancel job (§14). */
  jobCancelledExpired: (date: string): BandWords => ({
    lead: `Expired on ${date}, when the job was cancelled.`,
    rest: '',
  }),
  /** Grey — declined, on the link or by the office. */
  declined: (firstName: string | null | undefined, date: string, reason: string): BandWords => ({
    lead: `${Customer(firstName)} declined it on ${date}`,
    rest: `— ${said(reason)}`,
  }),
  /** Grey — every quote while the job is cancelled; the last sentence only when it can be reopened (§14). */
  jobCancelled: (date: string, why: string, canReopen: boolean): BandWords => ({
    lead: `The job was cancelled on ${date}`,
    rest: canReopen ? `— ${said(why)} Reopen the request to work on it again.` : `— ${said(why)}`,
  }),
}

// ===========================================================================
// Toasts (§5, §14) and the Proposal tab (§8 A, §14)
// ===========================================================================

/**
 * White toasts on the job's moves (findings §11; lead, Oct 30): §14's own
 * History sentences, word for word — no toast says anything History does not.
 * None for Cancel job. `quote` is *Q-2184 v2* (`quoteVersion`).
 */
export const TOAST = {
  /** Reopen request. */
  requestAgain: (job: string) => `${job} is a request again.`,
  /** Approve: *Q-2184 v2 approved by Karen Whitmer*. */
  approved: (quote: string, name: string) => `${quote} approved by ${name}`,
  /** Mark quoted. */
  markedQuoted: (quote: string) => `${quote} marked quoted`,
  /** Withdraw quote. */
  withdrawn: (quote: string) => `${quote} withdrawn`,
  /** Mark declined. */
  declined: (quote: string) => `${quote} declined`,
}

export const PROPOSAL_TAB = {
  /** The preview's sign area on a sent quote, where today's office approve was. */
  signsOnLink: (firstName: string | null | undefined) =>
    `${Customer(firstName)} signs on the link. To record a yes yourself, use Approve.`,
}

// ===========================================================================
// Board 3 A — Approve (480)
// ===========================================================================

/** How it came in (`quote_approvals.how`, `OFFICE_APPROVAL_HOW`), in the board's order. */
export const APPROVAL_HOW = {
  in_person: 'In person',
  phone: 'Phone',
  text: 'Text',
  email: 'Email',
} as const satisfies Record<OfficeApprovalHow, string>

export const APPROVE = {
  /** *Approve Q-2184 v2*. */
  title: (quote: string) => `Approve ${quote}`,
  who: 'Who approved',
  when: 'When',
  /** *Fri Oct 9 · 8:14 PM* — the day and the time, each already formatted. */
  whenValue: (day: string, time: string) => `${day} · ${time}`,
  how: 'How',
  /** *What Karen chose*. */
  chose: (firstName: string | null | undefined) => `What ${customer(firstName)} chose`,
  /** After an extra's name: *· optional · +$2,180.00*. */
  extra: (amount: string) => `· optional · +${amount}`,
  /** *What Karen said*. */
  said: (firstName: string | null | undefined) => `What ${customer(firstName)} said`,
  proof: 'Proof',
  attach: 'Attach a photo, screenshot or email',
  /** *Email Karen the approved quote to keep*. */
  email: (firstName: string | null | undefined) => `Email ${customer(firstName)} the approved quote to keep`,
  total: 'Approved total, before tax',
  /**
   * The box's sentence, in its weights: *Makes 2 visits —* **A** *and* **B**
   * *— and closes Q-2184 v3, a draft, as Replaced.* — the visits' names bold,
   * the joins regular. With no draft to close it ends after the names.
   */
  makes: (count: number, visits: readonly string[], drafts: readonly string[]) => {
    const { head, last } = andList(visits)
    return {
      /* The singular for one (lead, Oct 30): the board's sentence, its noun agreeing. */
      head: count === 1 ? `Makes ${count} visit — ` : `Makes ${count} visits — `,
      names: [...head, ...(last === null ? [] : [last])],
      /** Between two names: *, * and, before the last, * and *. */
      join: (index: number, of: number) => (index === of - 1 ? ' and ' : ', '),
      tail: drafts.length === 0
        ? '.'
        : ` — and closes ${drafts.map((draft) => `${draft}, a draft`).join(' and ')}, as Replaced.`,
    }
  },
  cancel: COMMON.cancel,
  approve: 'Approve',
}

// ===========================================================================
// Board 3 B — Mark quoted (460)
// ===========================================================================

/** How it went out (`quotes.sent_note`, `MARKED_HOW`), in the board's order. */
export const SENT_HOW = {
  handed_over: 'Handed over',
  own_email: 'Our own email',
  printed: 'Printed',
  other: 'Other',
} as const satisfies Record<MarkedHow, string>

export const MARK_QUOTED = {
  title: 'Mark quoted',
  /** *J-3271 · a quote went out another way*. */
  sub: (job: string) => `${job} · ${ACTION.markQuoted.job}`,
  which: 'Which quote',
  /** *draft · started Oct 1 by Tom Reyes*; *draft · started Oct 1* when nobody is named. */
  draft: (started: string, by?: string | null) => (by ? `draft · started ${started} by ${by}` : `draft · started ${started}`),
  how: 'How it went out',
  /** The green band, in its weights: **lead** *before* <i>Valid until</i> *after*. */
  band: (firstName: string | null | undefined) => ({
    lead: 'Marks it sent without emailing it.',
    before: `${Customer(firstName)}’s link works as if it had been sent, it counts from today for `,
    validUntil: 'Valid until',
    after: `, and any other version with ${customer(firstName)} expires. The job moves to Quoted.`,
  }),
  cancel: COMMON.cancel,
  markQuoted: 'Mark quoted',
}

// ===========================================================================
// Board 3 C — the company's setting (Settings › Customers › Proposal)
// ===========================================================================

export const APPROVAL_SETTING = {
  title: 'When the office approves for the customer',
  sub: 'Who approved and when are always recorded. Everything else can be skipped unless you require it.',
  how: { label: 'Require how it came in', sub: 'in person, phone, text or email' },
  note: { label: 'Require what the customer said', sub: 'a note in their words' },
  proof: { label: 'Require proof', sub: 'a photo, screenshot or email attached' },
}

// ===========================================================================
// Board 3 D — Withdraw quote (440)
// ===========================================================================

export const WITHDRAW = {
  /** *Withdraw Q-2184 v2*. */
  title: (quote: string) => `Withdraw ${quote}`,
  why: COMMON.why,
  band: (firstName: string | null | undefined): BandWords => ({
    lead: `${Customer(firstName)}’s link will say it has expired.`,
    rest: 'The job goes back to Requested; you can send a new version any time.',
  }),
  keep: 'Keep it',
  withdraw: 'Withdraw',
}

// ===========================================================================
// Board 3 E — Mark declined (460)
// ===========================================================================

export const MARK_DECLINED = {
  /** *Mark Q-2184 v2 declined*. */
  title: (quote: string) => `Mark ${quote} declined`,
  /** *Karen said no*. */
  sub: ACTION.markDeclined.sub,
  why: COMMON.why,
  /**
   * The five reasons the customer picks on the link — `DECLINE_REASONS`, the
   * one list (`shared/src/proposal.ts`), so the office and the link can never
   * offer different words.
   */
  reasons: DECLINE_REASONS as readonly { value: DeclineReason; label: string }[],
  /** *What Karen said*. */
  said: (firstName: string | null | undefined) => `What ${customer(firstName)} said`,
  band: {
    lead: 'The job moves to Declined.',
    rest: 'You can start a new quote, or reopen the request, later.',
  } satisfies BandWords,
  cancel: COMMON.cancel,
  markDeclined: 'Mark declined',
}

// ===========================================================================
// Board 3 F — Cancel job (440)
// ===========================================================================

/**
 * One line of *What this does*, in the board's runs: its bullet and words
 * `before`, then `bold` (a quote, or *Reopen request*) where the board draws
 * one, then `after`.
 */
export interface EffectWords { before: string; bold: string; after: string }

/** The bullet before each line — *· Q-2184 v2 expires …* */
const BULLET = '· '

export const CANCEL_JOB = {
  /** *Cancel J-3271*. */
  title: (job: string) => `Cancel ${job}`,
  /** *Paver patio, seat wall and fire pit · Quoted*. */
  sub: (name: string, status: string) => `${name} · ${status}`,
  why: COMMON.why,
  what: 'What this does',
  /** §14's five lines, in `GET /jobs/:id/cancel-preview`'s order, each after the board's bullet. */
  expires: (quote: string, firstName: string | null | undefined): EffectWords => ({
    before: BULLET, bold: quote, after: ` expires — ${customer(firstName)}’s link will say so`,
  }),
  draftStays: (quote: string): EffectWords => ({
    before: BULLET, bold: quote, after: ', a draft, stays — read-only while the job is cancelled',
  }),
  /** The singular for one (lead, Oct 30) — *1 visit is cancelled and comes off the schedule*. */
  visitsCancelled: (count: number): EffectWords => ({
    before: count === 1
      ? `${BULLET}${count} visit is cancelled and comes off the schedule`
      : `${BULLET}${count} visits are cancelled and come off the schedule`,
    bold: '',
    after: '',
  }),
  canReopen: { before: '· nothing is approved, so ', bold: ACTION.reopenRequest.label, after: ' can bring it back' } satisfies EffectWords,
  cannotReopen: {
    before: `${BULLET}a quote was approved, so it can’t be reopened — new work is a new job`, bold: '', after: '',
  } satisfies EffectWords,
  keep: 'Keep the job',
  cancel: ACTION.cancelJob.label,
}
